import { useEffect, useRef } from 'react';

/**
 * Custom hook to measure and log performance metrics
 * Usage: usePerformanceMonitor('ComponentName')
 */
export const usePerformanceMonitor = (componentName) => {
  const mountTime = useRef(Date.now());
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current += 1;
    const renderTime = Date.now() - mountTime.current;
    
    if (renderCount.current === 1) {
      console.log(`[Performance] ${componentName} - Initial render: ${renderTime}ms`);
      
      // Report to analytics if available
      if (window.gtag) {
        window.gtag('event', 'timing_complete', {
          name: componentName,
          value: renderTime,
          event_category: 'Component Load Time'
        });
      }
    }
  });

  const measureOperation = (operationName, fn) => {
    const start = Date.now();
    const result = fn();
    const duration = Date.now() - start;
    
    console.log(`[Performance] ${componentName}.${operationName}: ${duration}ms`);
    
    return result;
  };

  const measureAsync = async (operationName, fn) => {
    const start = Date.now();
    const result = await fn();
    const duration = Date.now() - start;
    
    console.log(`[Performance] ${componentName}.${operationName}: ${duration}ms`);
    
    // Report slow operations (> 1000ms)
    if (duration > 1000) {
      console.warn(`[Performance] Slow operation detected: ${componentName}.${operationName} took ${duration}ms`);
    }
    
    return result;
  };

  return { measureOperation, measureAsync };
};

/**
 * Hook to measure API call performance
 */
export const useAPIPerformance = () => {
  const measureAPICall = async (apiName, fetchFn) => {
    const start = performance.now();
    
    try {
      const result = await fetchFn();
      const duration = Math.round(performance.now() - start);
      
      console.log(`[API Performance] ${apiName}: ${duration}ms`);
      
      // Log slow API calls
      if (duration > 1000) {
        console.warn(`[API Performance] Slow API call: ${apiName} took ${duration}ms`);
      }
      
      // Store in session storage for debugging
      const perfData = JSON.parse(sessionStorage.getItem('api_performance') || '{}');
      if (!perfData[apiName]) {
        perfData[apiName] = { calls: 0, totalTime: 0, avgTime: 0, slowest: 0 };
      }
      
      perfData[apiName].calls += 1;
      perfData[apiName].totalTime += duration;
      perfData[apiName].avgTime = Math.round(perfData[apiName].totalTime / perfData[apiName].calls);
      perfData[apiName].slowest = Math.max(perfData[apiName].slowest, duration);
      
      sessionStorage.setItem('api_performance', JSON.stringify(perfData));
      
      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      console.error(`[API Performance] ${apiName} failed after ${duration}ms:`, error);
      throw error;
    }
  };

  const getPerformanceStats = () => {
    return JSON.parse(sessionStorage.getItem('api_performance') || '{}');
  };

  const clearPerformanceStats = () => {
    sessionStorage.removeItem('api_performance');
  };

  return { measureAPICall, getPerformanceStats, clearPerformanceStats };
};
